/**
 * formatValue — typed, generic attribute formatting for the farm twin
 * explorer's property grid (design doc §3.3).
 *
 * The merged ontology schema declares types (string / float64 / bool /
 * timestamp / enum / currency / json_array); attributes missing from the
 * schema still format best-effort from their runtime shape. Everything
 * renders de-DE. A null/empty value classifies as 'empty' — the grid
 * hides those rows behind the "leere Felder anzeigen" toggle.
 */

export type ValueKind = 'empty' | 'scalar' | 'array-scalar' | 'array-object' | 'object';

export interface SchemaFieldLike {
    id: string;
    type?: string;
    values?: string[];
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T|$)/;

export function classifyValue(value: unknown): ValueKind {
    if (value == null) return 'empty';
    if (Array.isArray(value)) {
        if (value.length === 0) return 'empty';
        const allObjects = value.every((v) => v != null && typeof v === 'object' && !Array.isArray(v));
        return allObjects ? 'array-object' : 'array-scalar';
    }
    if (typeof value === 'object') {
        // An object whose every value is empty carries no information —
        // treat it like a missing attribute (blank "Supplier" rows).
        const values = Object.values(value as Record<string, unknown>);
        if (values.length === 0) return 'empty';
        return values.some((v) => classifyValue(v) !== 'empty') ? 'object' : 'empty';
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '' || trimmed === '—' || trimmed === '–') return 'empty';
        return 'scalar';
    }
    return 'scalar';
}

function formatNumber(value: number, maxFractionDigits = 2): string {
    return value.toLocaleString('de-DE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: Number.isInteger(value) ? 0 : maxFractionDigits,
    });
}

/**
 * German dd.mm.yyyy for an ISO date or timestamp. When the string starts
 * with a ``YYYY-MM-DD`` prefix the calendar date is formatted DIRECTLY
 * from those digits — never via ``new Date(...).toLocaleDateString``,
 * whose zone shift drifts a late-UTC timestamp a day for the UTC+1/+2
 * (DE) audience. Non-ISO strings fall back to a lenient parse.
 */
export function formatDateString(value: string): string {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) {
        const [, y, m, d] = iso;
        return `${d}.${m}.${y}`;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Unit suffix heuristics driven by the attribute KEY (schema-poor data). */
function unitSuffix(fieldId: string): string {
    if (fieldId.endsWith('_kg_ha')) return ' kg/ha';
    if (/(^|_)area(_|$)|_ha$/.test(fieldId)) return fieldId.endsWith('_ha') ? ' ha' : '';
    if (/_pct$|_percent$/.test(fieldId)) return ' %';
    return '';
}

const NUMERIC_TYPE_RE = /^(float|int|double|decimal|number)/;

// Money-ish keys. Deliberately NOT a bare `value` match — measurement
// keys like ph_value would false-positive into euros.
const CURRENCY_KEY_RE = /(^|_)(amount|price|cost)s?($|_)|^total_value$/;

// Year keys (harvest_year, crop_year, …). A calendar year is an identifier,
// not a magnitude, so it must NOT get thousands grouping — 2022, never "2.022".
const YEAR_KEY_RE = /(^|_)(year|jahr)$/;

/**
 * Format a scalar attribute value for display. `field` (from the merged
 * ontology schema) refines the choice; without it the runtime shape and
 * key heuristics decide.
 */
export function formatScalar(value: unknown, fieldId: string, field?: SchemaFieldLike): string {
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
    // Facts often arrive as numeric STRINGS ("5.6"); when the schema says
    // the field is numeric, treat them as numbers. Never without schema —
    // identifiers like "01070654" must stay verbatim.
    if (
        typeof value === 'string' &&
        field?.type &&
        NUMERIC_TYPE_RE.test(field.type) &&
        value.trim() !== '' &&
        !Number.isNaN(Number(value))
    ) {
        value = Number(value);
    }
    if (typeof value === 'number') {
        if (field?.type === 'currency' || CURRENCY_KEY_RE.test(fieldId)) {
            return `${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
        }
        if (YEAR_KEY_RE.test(fieldId)) {
            return value.toLocaleString('de-DE', { useGrouping: false, maximumFractionDigits: 0 });
        }
        return `${formatNumber(value)}${unitSuffix(fieldId)}`;
    }
    const str = String(value).trim();
    if (field?.type?.startsWith('timestamp') || ISO_DATE_RE.test(str)) {
        return formatDateString(str);
    }
    return str;
}

/** Column priority for array-of-object mini tables (line_items & co.). */
const TABLE_COLUMN_PRIORITY = [
    'product_name',
    'quantity',
    'quantity_unit',
    'unit_price',
    'amount',
    'currency',
    'packaging',
    'pallet_count',
];
const TABLE_MAX_COLUMNS = 6;

export interface MiniTable {
    columns: string[];
    rows: string[][];
}

/**
 * Project an array-of-objects attribute (e.g. `line_items`) onto a small
 * table: prioritised known columns first, then remaining keys, capped at
 * TABLE_MAX_COLUMNS. Columns that are empty on EVERY row are dropped.
 */
export function toMiniTable(value: Array<Record<string, unknown>>): MiniTable {
    const keys = new Set<string>();
    for (const row of value) for (const k of Object.keys(row)) keys.add(k);
    const prioritized = TABLE_COLUMN_PRIORITY.filter((k) => keys.has(k));
    const rest = [...keys].filter((k) => !TABLE_COLUMN_PRIORITY.includes(k));
    const candidates = [...prioritized, ...rest];
    const nonEmpty = candidates.filter((k) => value.some((row) => classifyValue(row[k]) !== 'empty'));
    const columns = nonEmpty.slice(0, TABLE_MAX_COLUMNS);
    const rows = value.map((row) =>
        columns.map((k) => (classifyValue(row[k]) === 'empty' ? '' : formatScalar(row[k], k))),
    );
    return { columns, rows };
}

/** Prettify an unschema'd attribute key: "quality_class" → "Quality Class". */
export function prettifyKey(key: string): string {
    return key
        .split('_')
        .map((s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s))
        .join(' ');
}
